<template>
  <div
    v-hotkey="shortcuts"
    class="parsed-input"
    :class="{ hideCursor, compact }"
    @mousemove="mouseMove"
  >
    <div class="input" :class="{ escaped: escaped }">
      <div v-if="escaped" class="locked">
        <p v-if="!compact" ref="lockedElt" class="query url">
          {{ escaped }}
        </p>
        <p v-if="!compact" ref="lockedElt" class="query" />
      </div>
      <div class="query-wrapper">
        <p
          v-if="placeholder && !text"
          class="query placeholder-overlay"
          v-html="placeholder"
          @click="queryElt?.focus()"
        />
        <p
          ref="queryElt"
          contenteditable
          spellcheck="false"
          class="query"
          :class="{ isFocused }"
          @click.prevent
          @focus="onFocus"
          @blur="onBlur"
          @input="input"
          @keydown="keydown"
          @compositionstart="compositionStart"
          @compositionend="compositionEnd"
        />
      </div>
    </div>

    <!-- Region between the query input and completions. -->
    <slot name="belowInput" />

    <ul v-if="showsSuggestionList" class="completions" @mousedown.stop.prevent>
      <template v-if="completion">
        <template
          v-for="(completionDisplayed, i) in completionsDisplayed"
          :key="`completion-${i}-${completionDisplayed.text}`"
        >
          <Suspense>
            <ConsoleCompletion
              class="completion"
              :class="{ selected: i === selected }"
              :completion="completionDisplayed"
              :selected="i === selected"
              :no-completion-helpers="noCompletionHelpers"
              @mouseover="mouseOver(i)"
              @click="onCompletionClick"
            >
              <template #helpers="props">
                <slot
                  name="completion-helpers"
                  :completion="props.completion"
                />
              </template>
            </ConsoleCompletion>
          </Suspense>
        </template>
      </template>
      <template v-else-if="displayActions && displayedActions">
        <div
          v-for="(action, i) in displayedActions"
          :key="`${action.type}-${i}`"
        >
          <ConsoleActionComponent
            :action="action"
            :title="actionTitle(action)"
            :task="task"
            :context="context"
            :labels="queryLabels"
            :priority="queryPriority"
            :selected="i === selected"
            @mouseover="mouseOver(i)"
            @click="onActionClick"
            @dismiss="dismissCaptureAd"
          />
        </div>
      </template>
      <hr v-if="customCompletionsNumber() > 0" />
      <!-- This slot is intended to be used with <li>, please see an example in DateAndTime.vue before using it. -->
      <slot
        name="customCompletions"
        :mouse-over="mouseOverCustomCompletions"
        :selected="customCompletionsSelectedIndex"
      />
    </ul>
    <BaseNoResults
      v-else-if="noSearchResult"
      :is-loading="isLoading"
      class="no-results"
    />
  </div>
</template>

<script lang="ts">
import { debounce } from "lodash-es";
import { match } from "ts-pattern";
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
  VNode,
  watch,
} from "vue";

import controller from "@ctrl/controller";
import {
  ConsoleAction as ConsoleActionRpc,
  ConsoleParsingMode,
  GraphDatabase as ControllerDatabase,
  GeneralizedTask,
  GraphDatabase,
  ListNonempty,
  Mandate,
  MandateCompletion,
  MandateCompletionValue,
  MandateDestination,
  MandateParserWrapper,
  MandateParticipant,
  MandateParticipantLenient,
  RoutineMandateCompletion,
  RoutineMandateCompletions,
  RoutineMandateLabel,
  RoutineMandatePriority,
  TableDefaultExtensionsKind,
  Task,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import assert from "@/assert";
import BaseNoResults from "@/components/base/BaseNoResults.vue";
import {
  caretTargetNode,
  getCaretIndex,
  setCaretEnd,
  setCaretIndex,
} from "@/components/caret";
import {
  isCaptureAction,
  isCaptureActionHidden,
  useCaptureAdDismissed,
  useCaptureAvailability,
} from "@/components/console/capture";
import {
  canCreateNewInConsole,
  getBuiltinKind,
  isConsoleCreatableCustomType,
} from "@/components/databases";
import { findContextField } from "@/components/graph/context";
import * as inject from "@/components/inject";
import {
  deleteItem,
  redo,
  selectAll,
  ShortcutList,
  testShortcuts,
  undo,
  useModalOrInputShortcuts,
  validateAndOpen,
} from "@/components/shortcuts";
import { definitiveTableId } from "@/components/tables";
import { isWorkspaceWritable } from "@/composables/useProfile";
import { openPopoverCount } from "@/plugins/tippy-popover-count";
import { ConsoleAction, Context } from "@/types";
import { captureContextDefaultTitle } from "@/utils/context";
import { navigateToObject } from "@/utils/navigate";

import ConsoleActionComponent from "./ConsoleAction.vue";
import ConsoleCompletion from "./ConsoleCompletion.vue";
import { CreateObjectQuery, State } from "./parsed-input";
import {
  featuresForType,
  makeParser,
  makeParserForType,
  parseWithMode,
} from "./parsers";

const { consoleActions, mandateCompletionStopper } = controller;

function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

// Backend-suggested action types the console never surfaces (the frontend
// `ConsoleAction` type excludes them): `document-text` ("add text to note
// <doc>" for a `>` redirection) lost its UI when the action list was
// collapsed to a single primary creation action, and `capture-note`
// (untitled page holding the note body) was superseded by the titled
// "Create page" action.
type UnusedActionType = "document-text" | "capture-note";
const UNUSED_ACTION_TYPES: ReadonlySet<ConsoleActionRpc["type"]> = new Set([
  "document-text",
  "capture-note",
] satisfies UnusedActionType[]);

// The parser's suggestions minus the unused action types, narrowing from the
// RPC action type to the frontend `ConsoleAction`. The backend always
// suggests a creation action alongside the unused ones, so the result is
// never empty in practice.
function filterOutUnusedActions(
  actions: ListNonempty<ConsoleActionRpc>
): ListNonempty<ConsoleAction> | null {
  const kept = actions.filter(
    (action): action is Exclude<ConsoleActionRpc, { type: UnusedActionType }> =>
      !UNUSED_ACTION_TYPES.has(action.type)
  );
  const [first, ...rest] = kept;
  return first ? [first, ...rest] : null;
}

type ParseRequest = {
  mode: ConsoleParsingMode;
  locked: number;
  input: string;
  workspaceId: WorkspaceId | null;
};

export type CompletionFilter = (
  completion: MandateCompletion<
    MandateDestination,
    RoutineMandateLabel,
    MandateParticipantLenient<MandateParticipant>,
    RoutineMandatePriority,
    ControllerDatabase
  >
) => boolean;

export type UpdateObjectQuery = MandateCompletionValue<
  MandateDestination,
  RoutineMandateLabel,
  MandateParticipantLenient<MandateParticipant>,
  RoutineMandatePriority,
  ControllerDatabase
> & {
  kind: "update";
};
export type Query = CreateObjectQuery | UpdateObjectQuery;

const terminalCompletionStopper = new RegExp(`${mandateCompletionStopper}$`);
const completionDisambiguator = String.raw`[\u{E0100}-\u{E01EF}]`;
const completionDisambiguators = new RegExp(completionDisambiguator, "gu");

// Action types eligible as the single primary creation action of each
// built-in kind the console can create (`task-move` is a destination-mode
// action, never primary). The console shows type-specific pills for the
// selected type, so only one action of the right kind should survive.
const PRIMARY_ACTION_TYPES: Partial<
  Record<TableDefaultExtensionsKind, ReadonlySet<ConsoleAction["type"]>>
> = {
  task: new Set([
    "task",
    "task-allocation",
    "task-postponing",
    "task-scheduling",
    "task-recurrence",
  ]),
  event: new Set(["event-creation", "event-allocation"]),
  page: new Set(["page-creation"]),
};

export default defineComponent({
  components: {
    BaseNoResults,
    ConsoleActionComponent,
    ConsoleCompletion,
  },
  props: {
    context: { type: Object as PropType<Context>, default: null },
    providedInputValue: { type: String, required: false },
    compact: {
      type: Boolean,
      default: false,
    },
    completionFilter: {
      type: Function as PropType<CompletionFilter>,
      default: (
        completion: MandateCompletion<
          MandateDestination,
          RoutineMandateLabel,
          MandateParticipantLenient<MandateParticipant>,
          RoutineMandatePriority,
          GraphDatabase
        >
      ) =>
        // Offer exactly the types the console can create — the same set as
        // the pill type switcher (notably including prepackaged types like
        // Label, which are not user-defined).
        completion.value.type !== "type" ||
        canCreateNewInConsole(completion.value.value),
    },
    displayCompletions: {
      type: Boolean,
      default: true,
    },
    // Whether the action rows ("Create task …") are listed as suggestions.
    // The console turns them off in its create flow, where an explicit Add
    // button in the footer submits the (still computed) primary action;
    // keyword completions (`as <type>`, `#label`, …) keep showing either way.
    displayActions: {
      type: Boolean,
      default: true,
    },
    focused: Boolean,
    inputShortcuts: { type: Array as PropType<ShortcutList>, required: false },
    placeholder: { type: String, default: "" },
    noCompletionHelpers: {
      type: Boolean,
      default: false,
    },
    isRedirectionStopped: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    blur: () => true,
    clearContext: () => true,
    close: (_?: string) => true,
    completionSelected: (_completion: RoutineMandateCompletion["value"]) =>
      true,
    focus: () => true,
    escape: () => true,
    "update:text": () => true,
    "update:focused": (_: boolean) => true,
    "update:completions": () => true,
    open: () => true,
    spill: () => true,
    submit: (_: Query) => true,
    // Emitted when the parsed input resolves to (or clears) a `as <type>`
    // selection. Consumers (e.g. the console) decide what to render for it.
    typeChange: (_database: GraphDatabase | null) => true,
    // Emitted with the parsed mandate whenever it changes, so consumers can map
    // its keywords (dates, participants, …) onto the selected type's properties
    // live as the user types.
    mandateChange: (_mandate: Mandate | null) => true,
    validateCustomCompletion: (_index: number) => true,
  },
  setup(props, { emit, slots }) {
    const {
      completionFilter,
      context,
      displayActions,
      displayCompletions,
      focused,
      inputShortcuts,
      isRedirectionStopped,
      providedInputValue,
    } = toRefs(props);
    const controller = inject.controller();
    // Last `as <type>` selection emitted via `typeChange`, to avoid firing the
    // event on every keystroke while the same type stays selected.
    let lastEmittedTypeId: string | null = null;
    const emitTypeChange = (database: GraphDatabase | null) => {
      const id = database?.id ?? null;
      if (id === lastEmittedTypeId) return;
      lastEmittedTypeId = id;
      emit("typeChange", database);
    };
    // const parsers = inject.parsers();
    const parser = ref<MandateParserWrapper | null>(null);
    const isInputFocused = inject.isInputFocused();
    const locale = inject.locale();
    const router = inject.router();
    // For hiding gated capture actions once the user dismissed the capture
    // advertisement (a per-account local-storage preference, dismissed from
    // the cross on the gated action rows). A usable capture (entitled +
    // enabled plugin) is never hidden.
    const billing = inject.billing();
    const isContextualCaptureEnabled = inject.isContextualCaptureEnabled();
    const profile = inject.profile();
    const captureAvailability = useCaptureAvailability(
      billing,
      isContextualCaptureEnabled
    );
    const {
      captureAdDismissed,
      dismissCaptureAd,
      release: releaseCaptureAd,
    } = useCaptureAdDismissed(profile);
    onBeforeUnmount(releaseCaptureAd);
    // Template references
    const lockedElt = ref<HTMLElement | null>(null);
    const queryElt = ref<HTMLElement | null>(null);

    // Data
    const isFocused = ref(false);
    watch(isFocused, (v) => (isInputFocused.value = v));
    // Whether an IME composition is in progress in the query element.
    // Composition is a stateful, browser-owned edit of the DOM: rewriting
    // the element (or moving the selection) while it is active aborts it
    // and loses or duplicates the composed text.
    const composing = ref(false);
    onBeforeUnmount(() => (isInputFocused.value = false));
    const search = ref(false);
    const actions = ref<ListNonempty<ConsoleAction> | null>(null);
    const completion = ref<RoutineMandateCompletions | null>(null);
    // Completions to show when empty.
    const defaultCompletions =
      ref<ListNonempty<RoutineMandateCompletion> | null>(null);
    watch(providedInputValue, (v) => {
      if (queryElt.value && v) text.value = v;
    });
    // Slots
    // This cannot be a computed because slots.customCompletions() is
    // a func and Vue does not react to the output of the function
    // behing different.
    const customCompletionsNumber = () => {
      return slots.customCompletions && !!slots.customCompletions()
        ? slots
            .customCompletions()
            .filter((vnode: VNode) => vnode.el?.tag === "li").length
        : 0;
    };
    const customCompletionsSelectedIndex = computed(
      () => selected.value - (completionsDisplayed.value?.length || 0)
    );
    const validateCustomCompletion = (index: number) => {
      emit("validateCustomCompletion", index);
    };
    /// Immutable prefix that is not part of the query.
    const escaped = ref("");
    const mode = ref<ConsoleParsingMode>("generic");
    const hideCursor = ref(false);
    // The type the generic-mode parser is currently built for (null → default
    // Task). `activeParserKey` identifies its feature set so we only rebuild the
    // parser when the *features* change, not on every same-type reparse.
    const activeType = ref<GraphDatabase | null>(null);
    // The exact database picked through `setType` (the console's type pill
    // picker). Several types can share a name (e.g. the built-in Task and
    // every org workspace's provisioned Task type) and the parser resolves a
    // plain `as Task` to the *first* homonym; the disambiguator suffix that
    // pins the exact choice is only recoverable from the (truncated)
    // completion list, so text alone cannot always express the picked type.
    // While this override is set, a parse that resolved a homonym of it has
    // its type replaced with the override before actions, `query` and
    // `typeChange` are derived. Cleared when the clause resolves to another
    // name (or none) and when the user picks a type from the suggestion
    // list — an exact, disambiguated choice the override must not clobber.
    const typeOverride = ref<GraphDatabase | null>(null);
    const parserTypeKey = (type: GraphDatabase | null): string =>
      JSON.stringify(featuresForType(type));
    let activeParserKey = parserTypeKey(null);
    /// Immutable prefix that is part of the query.
    const locked = ref("");
    const query = ref<Mandate | null>(null);
    // Surface the parsed mandate to consumers (deduping is left to them); the
    // console maps its keywords onto the selected type's property pills.
    watch(query, (mandate) => emit("mandateChange", mandate));

    /// Index of the selected completion.
    const selected = ref(0);

    const task = ref<Task | GeneralizedTask>();
    const destinationWorkspaceId = ref<WorkspaceId | null>(null);
    const destinationKind = ref<"any" | "object">("any");
    const excludeDestinationId = ref<string | null>(null);
    const text = ref("");
    /// Title of the task (ie. everything not part of the query)
    const title = ref("");
    const typedTitle = computed<string | undefined>(() => {
      const typed = title.value
        .replaceAll(mandateCompletionStopper, "")
        .replace(completionDisambiguators, "")
        .trim();
      return typed || undefined;
    });
    // The context-derived default title (selection, tab name, …) only applies
    // to capture actions: the plain creation action advertised alongside a
    // capture one starts from a clean title.
    const actionTitle = (action: ConsoleAction): string | undefined => {
      if (typedTitle.value) return typedTitle.value;
      return context.value && isCaptureAction(action)
        ? captureContextDefaultTitle(context.value)
        : undefined;
    };
    const queryLabels = computed(() => query.value?.labels ?? []);
    const queryPriority = computed(() => query.value?.priority?.name);

    // Computed
    const canComplete = computed(() => !!completion.value || !!actions.value);
    // Whether the input holds an actual title. Drives the footer's Create
    // button: an attached context alone is not enough — plain creation stays
    // disabled until the user types a title (Capture uses `canCapture`
    // instead, where the context's default title counts).
    const canParse = computed<boolean>(() => !!typedTitle.value);
    const consoleMode = computed(
      () =>
        !!query.value?.dates || (completionsDisplayed.value?.length || 0) > 0
    );

    const destinationFilter = (c: RoutineMandateCompletion) => {
      if (c.value.type !== "destination") return false;
      const entry = c.value.value;
      if (destinationKind.value === "object") {
        if (entry.type !== "document") return false;
        if (!entry.id || entry.id.kind !== "object") return false;
        if (
          excludeDestinationId.value &&
          entry.id.id === excludeDestinationId.value
        )
          return false;
        return true;
      }
      const isCalendar = entry.type === "calendar";
      const isParent =
        entry.type === "document" &&
        task.value?.parent != null &&
        entry.id.kind === task.value.parent.kind &&
        entry.id.id === task.value.parent.id;
      const isSelf =
        entry.type === "document" &&
        entry.id.kind === "task" &&
        entry.id.id === task.value?.id;
      return !(isCalendar || isParent || isSelf);
    };
    const noSearchResult = computed<boolean>(
      () => mode.value === "destination" && !completionsDisplayed.value?.length
    );
    // `as <type>` completions create an object of that type, so types living
    // in a workspace the user cannot write are never offered — on top of the
    // consumer's own `completionFilter`, like the type pickers' writable-only
    // filter.
    const isCompletionWritable = (entry: RoutineMandateCompletion): boolean =>
      entry.value.type !== "type" ||
      isWorkspaceWritable(profile.value, entry.value.value.workspace);
    const completionsDisplayed = computed<RoutineMandateCompletion[]>(() => {
      if (mode.value === "destination")
        return completion.value?.completions.filter(destinationFilter) ?? [];
      if (!completion.value?.completions.length) return [];
      return (
        completion.value?.completions
          .filter(isCompletionWritable)
          // FIXME: completionFilter.value raise an error. Must be fixed
          //@ts-ignore
          .filter(completionFilter.value) ?? []
      );
    });
    // Event handling with electron is a complete shitshow when
    // opening/closing the window. When the window is reopened,
    // focus/blur events both fire half a dozen time. Ensure the
    // console stays blurred before actually sending the event.
    const debouncedBlur = debounce(() => {
      emit("blur");
    }, 0);
    const displayedActions = computed(() => {
      const modeActions = match(mode.value)
        .with(
          "date",
          "daytime",
          "destination",
          "person",
          "generic",
          () => actions.value
        )
        .with("plan", () =>
          actions.value?.filter(
            (action) =>
              action.type === "task-postponing" ||
              action.type === "task-scheduling" ||
              action.type === "task-allocation"
          )
        )
        .with("postpone", () =>
          actions.value?.filter((action) => action.type === "task-postponing")
        )
        .with("recurrence", () =>
          actions.value?.filter((action) => action.type === "task-recurrence")
        )
        .with("schedule", () =>
          actions.value?.filter(
            (action) =>
              action.type === "task-scheduling" ||
              action.type === "task-allocation"
          )
        )
        .exhaustive();
      // Once the capture advertisement is dismissed, gated capture actions
      // are not shown at all (a usable capture still is).
      const visible = modeActions?.filter(
        (action) =>
          !isCaptureActionHidden(
            captureAdDismissed.value,
            action,
            captureAvailability.value
          )
      );
      return visible && visible.length > 0 ? visible : null;
    });
    // The capture variant of the primary action, when the attached context can
    // be stored on the selected type (task url or custom-type context
    // property). The console's create flow keys off it: the footer shows a
    // secondary Capture button and Shift+Enter performs the capture (see
    // `submit`); plain Enter always creates. Removing the context (its
    // pill's cross) clears it back to null.
    const captureAction = computed<ConsoleAction | null>(
      () =>
        (context.value &&
          displayedActions.value?.find((action) => isCaptureAction(action))) ||
        null
    );
    // Whether a capture is submittable (the footer's Capture button and
    // Shift+Enter): unlike plain creation (`canParse`), an empty input is
    // enough — the capture then defaults its title from the context
    // (selection, tab name, …, see `actionTitle`). A typed title takes
    // precedence for both.
    const canCapture = computed<boolean>(
      () =>
        !!captureAction.value &&
        !!(
          typedTitle.value ||
          (context.value && captureContextDefaultTitle(context.value))
        )
    );
    // The non-capture variant of the primary action (plain creation without
    // storing the attached context). This is what Enter/Mod+Enter and the
    // footer's primary Create button submit, capture mode or not.
    const createAction = computed<ConsoleAction | null>(
      () =>
        displayedActions.value?.find((action) => !isCaptureAction(action)) ??
        null
    );
    // Dismissing the advertisement can shrink the list while a later action
    // is selected: keep the selection in bounds.
    watch(displayedActions, (list) => {
      if (list && selected.value >= list.length)
        selected.value = Math.max(list.length - 1, 0);
    });
    // Whether the suggestion list actually renders something: keyword
    // completions, action rows (unless suppressed via `displayActions`), or
    // slot-provided custom completions. Drives both the list's own `v-if` and
    // the console's `with-completions` styling, so no empty list shell ever
    // shows.
    const showsSuggestionList = computed(
      () =>
        (displayCompletions.value &&
          (!!completion.value ||
            (displayActions.value && !!displayedActions.value))) ||
        customCompletionsNumber() > 0
    );

    // Hooks
    onMounted(() => {
      if (lockedElt.value) lockedElt.value.innerHTML = `&nbsp;`;
    });

    const rebuildParser = async () => {
      const currentMode = mode.value ?? "generic";
      // The main create flow (generic mode) uses a type-driven parser: Task by
      // default, or the selected type's feature set. Sub-modes (date, schedule,
      // person, destination, …) keep their dedicated parsers.
      parser.value =
        currentMode === "generic"
          ? await makeParserForType(controller, activeType.value)
          : await makeParser(controller, currentMode);
    };

    // A click that dismisses an open pill popover (multi-select / relation)
    // lands on the completion/action row underneath it. Without this guard that
    // same click would submit and close the console. We snapshot whether a
    // popover was open in a document capture listener — registered at setup so
    // it runs before Tippy's own outside-click handler hides the popover and
    // decrements the count — then swallow the click that follows.
    let clickDismissesPopover = false;
    const onDocumentMousedownCapture = () => {
      clickDismissesPopover = openPopoverCount.value > 0;
    };
    document.addEventListener("mousedown", onDocumentMousedownCapture, {
      capture: true,
    });
    onBeforeUnmount(() =>
      document.removeEventListener("mousedown", onDocumentMousedownCapture, {
        capture: true,
      })
    );
    const onActionClick = () => {
      if (clickDismissesPopover) {
        clickDismissesPopover = false;
        return;
      }
      submit(false);
    };
    const onCompletionClick = () => {
      if (clickDismissesPopover) {
        clickDismissesPopover = false;
        return;
      }
      requestComplete(true);
    };

    const requestComplete = (stopper?: boolean, isOpeningItem = false) =>
      search.value ? openObject() : complete(stopper, isOpeningItem);
    const complete = (stopper = true, isOpeningItem = false) => {
      if (completion.value && displayCompletions.value) {
        const { replaced } = completion.value;
        const selectedCompletion =
          completionsDisplayed.value?.[selected.value] ??
          completion.value.completions[selected.value];
        if (selectedCompletion) {
          const { text: completed, value } = selectedCompletion;
          // A type completion is an exact, disambiguated choice: it
          // supersedes any type picked through `setType`.
          if (value.type === "type") typeOverride.value = null;
          match(mode.value)
            .with("date", "daytime", "person", () => {
              emit("submit", {
                kind: "update",
                ...value,
              });
            })
            .otherwise((mode) => {
              if (mode === "destination") {
                text.value = completed;
                completion.value = null;
              } else {
                // Hold my beer
                const utf16UnitCount =
                  replaced === 0
                    ? 0
                    : [...text.value]
                        .slice(-replaced)
                        .reduce((a, b) => a + b.length, 0);
                text.value =
                  (utf16UnitCount === 0
                    ? text.value
                    : text.value.slice(0, -utf16UnitCount)) + completed;
              }
              text.value =
                text.value +
                (stopper && completed !== ""
                  ? `${mandateCompletionStopper} `
                  : "");
              //FIXME: This should run when parsing, not when choosing a completion. Change this when parsed gives a result
              // (instead of a list of completions)
              if (
                selectedCompletion.value.type === "destination" &&
                selectedCompletion.value.value.type === "document" &&
                (destinationKind.value !== "object" ||
                  selectedCompletion.value.value.id.kind === "object") &&
                queryElt.value
              ) {
                actions.value = [
                  {
                    type: "task-move",
                    document: {
                      id: selectedCompletion.value.value.id,
                      section: selectedCompletion.value.value.section,
                    },
                  },
                ];

                queryElt.value.innerHTML = text.value;
                title.value = escaped.value;
                query.value = {
                  destination: {
                    type: "document",
                    id: selectedCompletion.value.value.id,
                    section: selectedCompletion.value.value.section,
                  },
                };
                selected.value = 0;
              }
              if (queryElt.value) setCaretEnd(queryElt.value);
            });
        }
      } else if (isOpeningItem) {
        emit("open");
      }
    };
    const fill = async (args: State) => {
      search.value = args.mode === "search";
      escaped.value = args.escaped || "";
      locked.value = match(args.mode).otherwise(() => args.locked || "");
      mode.value =
        args.mode === "search" ? "destination" : args.mode || "generic";
      task.value = args.task || undefined;
      text.value = args.text || "";
      destinationWorkspaceId.value = args.workspaceId ?? null;
      destinationKind.value = args.destinationKind ?? "any";
      excludeDestinationId.value = args.excludeDestinationId ?? null;

      // Allow `mode` update to propagate.
      await nextTick();
      if (!parser.value) {
        await rebuildParser();
      }
      defaultCompletions.value = args.task
        ? await match(args.mode)
            .with(
              "postpone",
              async () =>
                await controller.mandate
                  .parse(parser.value!, 0, "in ")
                  .then((result) => result?.completions?.completions || null)
            )
            .with(
              "schedule",
              async () =>
                await controller.mandate
                  .parse(parser.value!, 0, "on ")
                  .then((result) => result?.completions?.completions || null)
            )
            .otherwise(() => null)
        : null;

      // Ensure we reparse even if text didn't change, so that locked
      // HTML is recomputed.
      await parse();
      rebuildParser();
    };

    // No need for refs here.
    let pendingParseRequest: ParseRequest | null = null;
    let busy = false;
    let parsePromise: ReturnType<typeof parseWithMode> | null = null;
    const isLoading = ref(false);
    const parse = async () => {
      // Queue up our call, overriding any other pending call.
      isLoading.value = true;
      pendingParseRequest = {
        mode: mode.value ?? "generic",
        locked: byteLength(locked.value),
        input: locked.value + text.value,
        workspaceId: destinationWorkspaceId.value,
      };
      // If `busy` is set, another parse() call already took over
      // responsibility for handling queued calls, and will eventually handle
      // the parse request we just set as pending (if it is not overriden in the
      // meantime).
      if (busy) {
        return;
      }

      // Whenever a parsing request completes, we must check if another has been
      // submitted in the meantime, and keep doing this until the queue clears
      // up.
      assert(pendingParseRequest != null);
      busy = true;
      let currentParseReq: ParseRequest;
      let parsed: Awaited<ReturnType<typeof parseWithMode>>;
      do {
        assert(busy);
        currentParseReq = pendingParseRequest;
        pendingParseRequest = null;
        if (!parser.value) {
          await rebuildParser();
        }
        parsePromise = parseWithMode(
          controller,
          parser.value!,
          currentParseReq.mode,
          currentParseReq.input,
          currentParseReq.locked,
          undefined,
          currentParseReq.workspaceId
        );
        parsed = await parsePromise;
      } while (pendingParseRequest != null);
      busy = false;

      // The OCaml assumes UTF-8 for now.
      const el = queryElt.value;

      // Track the `as <type>` selection resolved by this parse; the classic
      // branch sets it when a type is selected. Emitted (deduped) at the end.
      let resolvedType: GraphDatabase | null = null;

      match(mode.value)
        .with("date", "daytime", "person", "destination", (mode) => {
          if (mode !== "destination") actions.value = null;
          // this was the old parsing method for these, this code should never be called
          if (parsed && "html_free" in parsed) {
            if (parsed.completions) {
              completion.value = parsed.completions;
            } else completion.value = null;
            // This is the new parsing method with complete.participants etc
          } else if (Array.isArray(parsed) && parsed.length > 0) {
            const [first, ...rest] = parsed;
            if (first) {
              completion.value = {
                completions: [first, ...rest],
                replaced: 0,
              };
            }
          }
        })
        .otherwise(() => {
          // We should be in the parse classic function so this should always be true
          if (parsed && "html_free" in parsed) {
            const {
              result: parsedResult,
              html_locked: htmlLocked,
              html_free: htmlFree,
            } = parsed;
            // Apply the picked-type override (see `typeOverride`): a clause
            // resolved to a homonym of the picked type is redirected to the
            // exact picked database; a clause resolved to another name (or
            // removed) means the user switched types through the input, and
            // the override no longer applies.
            let result = parsedResult;
            const override = typeOverride.value;
            if (override) {
              if (result.type?.data_name === override.data_name) {
                if (
                  definitiveTableId(result.type.id) !==
                    definitiveTableId(override.id) ||
                  result.type.workspace !== override.workspace
                )
                  result = { ...result, type: override };
              } else {
                typeOverride.value = null;
              }
            }
            title.value = escaped.value + parsed.title;
            // Drop the backend suggestions the console never surfaces right
            // at the parse boundary (see `UNUSED_ACTION_TYPES`).
            actions.value = filterOutUnusedActions(
              consoleActions(null, title.value, result, !!context.value)
            );
            //FIXME: GRAPH Discuss actions that should be returned by the back-end
            const firstAction = actions.value?.[0];
            const firstActionDatabase =
              firstAction?.type === "custom-type" ||
              firstAction?.type === "task"
                ? (firstAction.database ?? null)
                : null;
            // The explicitly selected custom type (`as <Type>`, user-defined
            // or prepackaged like Label) drives creation regardless of any
            // date/participant/duration keywords. The backend would otherwise
            // turn those keywords into task/event scheduling actions that
            // drop the custom type; we keep the type as the source of truth
            // and let the keywords map onto its properties. Built-in types
            // still follow the backend's actions so their scheduling/
            // allocation semantics stay intact.
            const database =
              result.type && isConsoleCreatableCustomType(result.type)
                ? result.type
                : firstActionDatabase;
            if (database) {
              // Built-in (task/page/event) and user-defined types are all
              // created through the custom-type action; performAction routes
              // each to the right RPC. A passed context (contextual capture)
              // offers a second "Capture <type>" action for the types that
              // can hold a context: a task (stored as its url) or a custom
              // type with a context property.
              const supportsContext =
                !!context.value &&
                (getBuiltinKind(database) === "task" ||
                  !!findContextField(database));
              actions.value = supportsContext
                ? [
                    { type: "custom-type", database, context: false },
                    { type: "custom-type", database, context: true },
                  ]
                : [{ type: "custom-type", database, context: false }];
              // Surface the resolved `as <type>` selection to consumers.
              resolvedType = database;
            } else if (result.type) {
              // A built-in `as <type>` (e.g. Event) whose actions come from
              // the backend rather than the custom-type wrapper: keep those
              // actions but still surface the type to consumers (pills,
              // parser feature selection).
              resolvedType = result.type;
            }
            // The console shows type-specific pills for the selected type
            // (the default Task, or a built-in picked via `as <Type>`), so
            // the backend's alternative suggestions ("plan for Monday",
            // "create page", ...) are redundant: keep a single primary
            // creation action of the selected kind, plus its capture
            // variants when present. Custom types are already reduced to
            // their single custom-type action above.
            const allActions = actions.value;
            if (
              !database &&
              allActions &&
              (mode.value ?? "generic") === "generic"
            ) {
              // No resolved `as <type>` means the console's default Task flow.
              const kind = resolvedType ? getBuiltinKind(resolvedType) : "task";
              const eligible = kind ? PRIMARY_ACTION_TYPES[kind] : undefined;
              const primary = eligible
                ? allActions.find((action) => eligible.has(action.type))
                : undefined;
              if (primary) {
                actions.value = [
                  primary,
                  ...allActions.filter(
                    (action) =>
                      action !== primary && action.type === primary.type
                  ),
                ];
              }
              // With a context but no typed title, the backend only suggests
              // the capture variant; the console still advertises the plain
              // creation first (with a clean title — only capture actions
              // default their title from the context).
              const [first, ...rest] = actions.value ?? [];
              if (
                first &&
                "capture" in first &&
                first.capture &&
                !rest.some(
                  (action) =>
                    action.type === first.type &&
                    "capture" in action &&
                    !action.capture
                )
              ) {
                actions.value = [{ ...first, capture: false }, first, ...rest];
              }
            }
            if (defaultCompletions.value === null || text.value.length > 0) {
              completion.value = parsed.completions || null;
            }

            query.value = result;
            selected.value = 0;
            if (htmlLocked && lockedElt.value) {
              lockedElt.value.innerHTML = `&nbsp;${htmlLocked.join("")}`;
            }

            const newHtml = htmlFree.join("");

            // A parse landing while an IME composition is active must not
            // touch the query element: both the caret probe below and the
            // innerHTML rewrite abort the composition and lose the composed
            // text. The compositionend-triggered reparse refreshes the
            // markup right after.
            if (composing.value) {
              // Nothing to patch.
            } else if (focused.value) {
              // This is null if the cursor is at the end of the prompt,
              // a character index otherwise.
              const position = (() => {
                if (el) {
                  const pos = getCaretIndex(el);
                  setCaretEnd(el);
                  if (getCaretIndex(el) == pos) return null;
                  else {
                    return pos;
                  }
                }
              })();
              if (el) {
                el.innerHTML = newHtml;
                if (position) {
                  setCaretIndex(el, position);
                } else {
                  setCaretEnd(el);
                }
              }
            } else {
              if (el) el.innerHTML = newHtml;
            }
          }
        });
      emitTypeChange(resolvedType);
      // Realtime parser selection (generic create flow only): when the resolved
      // `as <type>` maps to a different feature set, rebuild the parser and
      // reparse once so the new keyword set applies immediately. `type_` parses
      // regardless of features, so the resolved type is stable across the swap
      // and this converges after a single reparse.
      if (mode.value === "generic") {
        const nextKey = parserTypeKey(resolvedType);
        if (nextKey !== activeParserKey) {
          activeParserKey = nextKey;
          activeType.value = resolvedType;
          await rebuildParser();
          await parse();
          return;
        }
      }
      isLoading.value = false;
    };

    const submit = async (
      isOpeningItem = false,
      isClosingConsole = true,
      withContext?: boolean
    ) => {
      if (!displayedActions.value || displayedActions.value.length === 0)
        return;
      assert(query.value);
      // With the action rows hidden (the console's create flow) the user
      // can't pick an action: Enter/Mod+Enter and the primary Create button
      // submit the plain creation; Shift+Enter and the secondary Capture
      // button pass `withContext: true` to perform the capture instead.
      // Both are gated like their footer buttons (`canParse`/`canCapture`),
      // so the keyboard can't submit what the disabled buttons refuse —
      // notably an untitled plain creation on an empty input.
      let action: ConsoleAction;
      if (!displayActions.value) {
        if (withContext === true) {
          const capture = captureAction.value;
          if (!capture || !canCapture.value) return;
          action = capture;
        } else {
          if (!canParse.value) return;
          action = createAction.value ?? displayedActions.value[selected.value];
        }
      } else {
        action = displayedActions.value[selected.value];
      }
      emit("submit", {
        kind: "create",
        action,
        query: query.value,
        // Destination-mode moves (e.g. custom object move) don't always have a title/context.
        // The backend action handler for moves doesn't rely on this field.
        title: actionTitle(action) ?? "",
        // Only capture actions store the context; a plain creation submitted
        // while a context is attached must not carry it along.
        context: isCaptureAction(action) ? context.value : undefined,
        task: task.value,
        options: {
          isOpeningItem,
          isClosingConsole,
        },
      });
    };

    // Switch the create target type programmatically (from the console's type
    // pill picker), by rewriting the input's `as <Type>` clause — the parser
    // stays the single source of truth for the selected type, actions, and
    // keyword features. The text watcher reparses after the rewrite.
    const escapeRegExp = (s: string): string =>
      s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // The exact spelling of `database` for the parser. Several types can
    // share a name (e.g. the built-in Task and an org workspace's
    // provisioned Task type): the parser then suffixes each completion with
    // an invisible disambiguator (see `completionDisambiguator`) and a plain
    // name resolves to the first homonym. Complete the clause and pick the
    // target database's completion — its text carries the disambiguator the
    // reparse needs to resolve to that exact type. Best-effort: the
    // completion list is truncated (see `refine_completions` in the
    // controller), so with many homonyms the target may be missing and the
    // plain name is inserted — `typeOverride` then redirects the parse's
    // homonym resolution to the picked database.
    const disambiguatedTypeName = async (
      database: GraphDatabase,
      name: string
    ): Promise<string> => {
      if (!parser.value) await rebuildParser();
      const parsed = await controller.mandate.parse(
        parser.value!,
        0,
        `as ${name}`
      );
      const targetId = definitiveTableId(database.id);
      const completion = parsed?.completions?.completions.find(
        (candidate) =>
          candidate.value.type === "type" &&
          definitiveTableId(candidate.value.value.id) === targetId &&
          candidate.value.value.workspace === database.workspace
      );
      return completion?.text ?? name;
    };
    const setType = async (database: GraphDatabase | null, asName?: string) => {
      typeOverride.value = database;
      // Resolve the disambiguated name before reading `text`, so the rewrite
      // below stays synchronous and cannot race a concurrent edit.
      const name = database
        ? await disambiguatedTypeName(
            database,
            asName ?? (database.data_name || database.name)
          )
        : null;
      const stopper = mandateCompletionStopper;
      let next = text.value;
      // Strip the current clause, as the parser completed it: the `as`
      // keyword, the type name, and (usually) the completion stopper. The
      // parsed type's data_name/name are the candidates for how the clause
      // was spelled.
      const current = query.value?.type;
      const candidates = current
        ? [...new Set([current.data_name, current.name])].filter(
            (name): name is string => !!name
          )
        : [];
      for (const candidate of candidates) {
        const clause = new RegExp(
          `(?:^|\\s)as\\s+${escapeRegExp(
            candidate
          )}${completionDisambiguator}*(?:${escapeRegExp(stopper)})?\\s*`,
          "iu"
        );
        if (clause.test(next)) {
          next = next.replace(clause, " ");
          break;
        }
      }
      next = next.replace(/\s+$/, "");
      if (name !== null) {
        next = `${next} as ${name}${stopper} `.trimStart();
      } else if (next !== "") {
        next = `${next} `;
      }
      text.value = next;
    };

    const openObject = () => {
      emit("close");
      const selectedCompletion = completionsDisplayed.value?.[selected.value];
      if (selectedCompletion) {
        emit("completionSelected", selectedCompletion.value);
        if (
          selectedCompletion.value.type === "destination" &&
          !(typeof selectedCompletion.value.value.id === "string") &&
          !isRedirectionStopped.value
        ) {
          navigateToObject(
            router,
            controller,
            selectedCompletion.value.value.id
          );
        }
      }
    };

    // Watchers
    watch(
      // Not watching `focused` is intended. `context` matters because the
      // capture action derives from `!!context.value` *at parse time*: in
      // the dashboard the context arrives asynchronously (~400ms after the
      // open, via `update-context`), and without a reparse the capture
      // action — and with it the context pill and the Capture button —
      // would only ever appear through an incidental focus-triggered parse.
      [text, task, mode, locked, escaped, context],
      () => {
        parse();
      }
    );
    // Prefill an untouched input with the captured selection (collapsed to
    // one line): unlike tab/app names — which only ever default the *capture*
    // title at submit time — selected text is content the user deliberately
    // picked, so it lands in the input where it can be edited, and both
    // Create and Capture then use it like typed text. Anything already in
    // the input (typed or restored from the last open) is never clobbered.
    watch(context, (ctx) => {
      const selection = ctx?.selection?.replace(/\s+/g, " ").trim();
      if (!selection) return;
      if (mode.value !== "generic") return;
      if (text.value.trim()) return;
      text.value = selection;
    });
    watch(
      () => {
        return { text: text.value, completions: defaultCompletions.value };
      },
      ({ text, completions }) => {
        if (text === "" && completions && completions.length > 0) {
          completion.value = {
            completions,
            replaced: 0,
          };
        } else if (text === "") {
          completion.value = null;
        }
      }
    );
    watch(completion, () => {
      hideCursor.value = true;
    });

    const areShortcutsEnabled = computed(
      () => isInputFocused.value && isFocused.value
    );
    const { shortcuts } = useModalOrInputShortcuts(
      inputShortcuts,
      areShortcutsEnabled
    );
    const navigateUp = () => {
      selected.value = Math.max(selected.value - 1, 0);
    };
    const navigateDown = () => {
      if (completion.value || customCompletionsNumber() > 0) {
        selected.value = Math.min(
          selected.value + 1,
          (completionsDisplayed.value?.length || 0) +
            customCompletionsNumber() -
            1
        );
      } else if (displayedActions.value) {
        // Bound by the displayed list, which can be shorter than the raw
        // actions (mode filtering, dismissed capture advertisement).
        selected.value = Math.min(
          selected.value + 1,
          displayedActions.value.length - 1
        );
      }
    };
    return {
      actionTitle,
      actions,
      canCapture,
      canComplete,
      canParse,
      captureAction,
      createAction,
      complete,
      completion,
      showsSuggestionList,
      completionsDisplayed,
      consoleMode,
      customCompletionsNumber,
      customCompletionsSelectedIndex,
      debouncedBlur,
      defaultCompletions,
      dismissCaptureAd,
      displayedActions,
      escaped,
      fill,
      hideCursor,
      isFocused,
      isLoading,
      locale,
      locked,
      lockedElt,
      mode,
      navigateDown,
      navigateUp,
      noSearchResult,
      onActionClick,
      composing,
      onCompletionClick,
      openObject,
      parse,
      query,
      queryElt,
      queryLabels,
      queryPriority,
      rebuildParser,
      requestComplete,
      search,
      selected,
      setType,
      shortcuts,
      submit,
      task,
      text,
      title,
      typeOverride,
      validateCustomCompletion,
    };
  },
  methods: {
    cancel(e: Event) {
      e.stopPropagation();
      const { completion, text, title } = this;
      const replaced = completion?.replaced;
      // If we press escape and there are completions, block
      // completions by inserting the completion blocker one character
      // before the completion beginning, hopefully just after the
      // keyword. If that position is out of the editable text
      // (eg. when rescheduling a task with the console), interpret it
      // as a close instruction, since the only purpose of that
      // console instance is to offer completions.
      const codepoints = [...text];
      if (
        replaced !== undefined &&
        replaced < codepoints.length &&
        !this.search
      ) {
        const n = replaced || 1;
        const utf16UnitCount = codepoints
          .slice(-n)
          .reduce((a, b) => a + b.length, 0);
        this.text =
          text.slice(0, -utf16UnitCount) +
          mandateCompletionStopper +
          text.slice(-utf16UnitCount);
      } else {
        // If we press escape while the caret is in a tag, escape that
        // tag.
        const el = this.queryElt;
        const node = caretTargetNode()?.parentNode;
        if (this.text !== "" && el && node instanceof Element && node !== el) {
          const pos = (getCaretIndex(el) ?? 0) - (getCaretIndex(node) ?? 0);
          this.text =
            text.slice(0, pos) + mandateCompletionStopper + text.slice(pos);
        } else {
          this.text = title
            .replaceAll(mandateCompletionStopper, "")
            .replace(completionDisambiguators, "")
            .trim();
          this.$emit("close");
        }
      }
    },
    clear(focus?: boolean) {
      this.actions = null;
      this.completion = null;
      this.defaultCompletions = null;
      this.escaped = "";
      this.locked = "";
      this.query = null;
      this.search = false;
      this.selected = 0;
      this.text = "";
      this.task = undefined;
      this.typeOverride = null;
      this.mode = "generic";
      if (this.lockedElt) this.lockedElt.innerHTML = `&nbsp;`;
      if (this.queryElt) {
        this.queryElt.innerHTML = "";
        if (focus) {
          this.queryElt.focus();
        }
      }
      this.$emit("clearContext");
    },
    compositionStart() {
      this.composing = true;
    },
    compositionEnd(e: Event) {
      this.composing = false;
      // Every `input` event of the composition — including the commit's —
      // is flagged as composing and ignored by `input()`: read the
      // committed text here instead.
      void this.input(e);
    },
    focus() {
      this.debouncedBlur?.cancel();
      this.queryElt?.focus();
      // Required when reopening the dashboard so the field is
      // actually focused. Something seems to be stealing the focus
      // after the page is re-displayed.
      setTimeout(() => {
        this.queryElt?.focus();
      }, 0);
    },
    async input(e: Event) {
      if (e instanceof InputEvent && e.isComposing) return;
      if (!(e.target instanceof HTMLElement)) return;
      const el = e.target;
      // The input is single-line: pasted newlines are flattened to spaces
      // (nothing consumes multi-line text since the multiline page console
      // was removed).
      this.text = el.innerText
        .replace(/^\n$/, "")
        .replace(/\n/g, " ")
        .replace(terminalCompletionStopper, "")
        .replace(/\u00A0/g, " ");
    },
    keydown(event: KeyboardEvent): void {
      if (event.isComposing) {
        return;
      }
      if (testShortcuts(ref([undo.value, redo.value]), event)) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (testShortcuts(deleteItem, event) || testShortcuts(selectAll, event)) {
        event.stopPropagation();
        return;
      }
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          event.stopPropagation();
          this.navigateUp();
          break;
        case "ArrowDown":
          event.preventDefault();
          event.stopPropagation();
          this.navigateDown();
          break;
        case "Enter":
          {
            const isShiftEnter = testShortcuts(ref("shift+enter"), event);
            // Mod+Shift+Enter is a capture combo only in the create flow
            // (action rows hidden); with the rows visible it falls through
            // to the generic Enter handling below.
            const isModShiftEnter =
              !this.displayActions &&
              testShortcuts(ref("mod+shift+enter"), event);
            if (!isShiftEnter && !isModShiftEnter) {
              event.preventDefault();
              event.stopPropagation();
              const isAltEnter = testShortcuts(ref("alt+enter"), event);
              const isMetaEnter = testShortcuts(validateAndOpen, event);
              if (!this.completion) {
                this.submit(isMetaEnter, !isAltEnter);
                if (isAltEnter) this.clear();
              } else if (
                this.customCompletionsNumber() > 0 &&
                this.completion.completions.length <= this.selected
              ) {
                this.validateCustomCompletion(
                  this.selected - this.completion.completions.length
                );
              } else this.requestComplete(true, event.metaKey);
            } else {
              // Shift+Enter performs the contextual capture in the create
              // flow (Enter is the plain creation), matching the footer's
              // secondary Capture button; Mod+Shift+Enter also opens the
              // created item, mirroring Mod+Enter for plain creation.
              // Always consumed: Shift+Enter used to insert a newline and
              // hand the input over to a multiline page console, but that
              // path is gone and the input is single-line.
              event.preventDefault();
              event.stopPropagation();
              if (!this.displayActions && this.canCapture)
                this.submit(isModShiftEnter, true, true);
            }
          }
          break;
        case "Tab":
          if (this.completion && this.mode === "generic") {
            event.preventDefault();
            event.stopPropagation();
            this.complete(false);
          }
          break;
        case "Escape":
          event.preventDefault();
          this.$emit("escape");
          this.cancel(event);
          break;
      }
    },
    mouseOver(index: number) {
      if (!this.hideCursor) this.selected = index;
    },
    mouseOverCustomCompletions(index: number) {
      if (!this.hideCursor)
        this.selected = (this.completion?.completions.length || 0) + index;
    },
    mouseMove() {
      this.hideCursor = false;
    },
    onBlur() {
      this.isFocused = false;
      this.completion = null;
      this.$emit("update:focused", false);
      this.debouncedBlur();
    },
    onFocus() {
      this.isFocused = true;
      this.debouncedBlur?.cancel();
      this.$emit("update:focused", true);
      this.$emit("focus");
      this.parse();
    },
  },
});
</script>

<style scoped lang="scss">
.parsed-input {
  display: flex;
  flex-direction: column;
  width: 100%;

  &.compact {
    .completions {
      width: 100%;
      z-index: $z-index-content;
      max-width: 340px;
    }

    :deep(.console-completion) {
      padding-top: 4px;
      padding-bottom: 6px;

      .text {
        @extend %font-caption-regular;
      }

      .helper .helper-text {
        @extend %font-small-medium;

        @include themed() {
          color: t($grey-40);
        }
      }
    }
  }

  &.hideCursor * {
    cursor: none !important;
  }

  .input {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    &.escaped {
      flex-direction: column;
      row-gap: 8px;

      .locked p.query:first-child,
      .locked p.query.url {
        height: 17px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-bottom: 4px;

        @include themed() {
          border-bottom: 1px solid t($grey-10);
        }
      }
    }

    .locked {
      display: flex;
      flex-direction: row;

      .icon {
        margin-top: 1px;
        margin-inline-end: 9px;

        &,
        :deep(img) {
          height: 18px;
          width: 18px;
        }
      }

      p.query {
        cursor: default;
        height: auto;
        flex-grow: 0;
        @extend %font-caption-regular;
        padding: 0 0 4px 0;

        @include themed() {
          color: t($grey-70);
        }
      }
    }

    :not(.locked) p.query {
      margin-inline-end: 24px;
      overflow: visible;
      cursor: text;
    }

    p.query {
      outline: none;
      flex-grow: 1;
      flex-shrink: 0;
      margin: 0;
      overflow: hidden;
      white-space: nowrap;
      min-width: 1px; // Make sure the caret is always visible.

      &:not(.placeholder-overlay) :deep(.keyword) {
        font-weight: 400;

        @include themed() {
          color: t($grey-50);
        }
      }

      :deep(.value) {
        font-weight: 400;

        @include themed() {
          color: t($secondary);
        }
      }
    }
  }

  .completions {
    margin: 0;
    padding: 0;

    hr {
      height: 1px;
      border: none;
      margin: 6px 8px;

      @include themed() {
        background: t($grey-15);
      }
    }
  }

  .no-results {
    margin-top: 14px;
    padding: 20px 16px;

    @include themed() {
      border-top: 1px solid t($grey-10);
    }
  }

  .query-wrapper {
    display: grid;
    flex-grow: 1;
    flex-shrink: 0;
    min-width: 1px;

    > * {
      grid-area: 1 / 1;
    }
  }

  .placeholder-overlay {
    pointer-events: none;
    background-color: transparent;
    @include themed() {
      color: t($grey-40);
    }

    &.query :deep(.keyword) {
      opacity: 0.5;
      font-weight: 500;
      @include themed() {
        color: t($secondary);
      }
    }
  }

  .console-action.selected,
  .completion.selected {
    border-radius: $border-radius-m;

    @include themed() {
      background: t($grey-10);
    }

    &:active {
      @include themed() {
        background: t($grey-15);
      }
    }
  }

  .completion:not(.selected) {
    @include themed() {
      color: t($grey-70);
    }
  }
}
</style>
